import { useMemo } from 'react'

import type { FilterControlProps } from 'features/charts-v2'
// bully ignore[no-cross-feature-private-imports: same established seam use-keywords-filter-sidebar.ts uses for the sibling filters hook. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { useDefaultFilterListLoading } from 'features/filters/hooks/use-default-filter-list-loading'
import { useFilterUIStore, type FilterUIState } from 'features/filters/store'
import {
  buildFilterControlProps,
  buildSelectionsByField,
  buildUpdateFilterSelectionHandler,
} from 'features/trend-detection/features/agent-create/hooks/agent-filter-panel-handlers'
import { narrowAgentFilterOptions } from 'features/trend-detection/features/agent-create/utils/agent-filter-criteria'
import type {
  FilterCriterion,
  StaleByField,
} from 'features/trend-detection/types'
import { unavailableLabel } from 'features/trend-detection/utils/filter-value-list'

export interface UseAgentFilterPanelParams {
  projectId: string | null
  criteria: FilterCriterion[]
  onCriteriaChange: (criteria: FilterCriterion[]) => void
  /** Server's staleness verdict keyed by field. Present callers surface a
   *  warning badge on the affected card (see `FilterCard`'s `stale` prop). */
  staleByField?: StaleByField
  /** Server's name for each saved theme uuid that still resolves. Names the
   *  live uuids the project's filter options omit, which would otherwise
   *  render raw on their card; a theme uuid absent from the map renders as
   *  `[Deleted Value]`. `null`/absent means no map (not fetched, or the
   *  lookup failed), so nothing is labelled deleted. */
  valueLabels?: Readonly<Record<string, string>> | null
}

export interface UseAgentFilterPanelResult {
  filterControlProps: FilterControlProps
}

/** Adapts an agent's flat `FilterCriterion[]` into what `FilterControl` needs
 * and back. Reads `defaultFilterList` directly instead of going through
 * `useEffectiveFilterList` (whose `usePanelFilterOptions` call is
 * `enabled: false` for null-panel callers, so it would never populate). */
export function useAgentFilterPanel({
  projectId,
  criteria,
  onCriteriaChange,
  staleByField,
  valueLabels,
}: UseAgentFilterPanelParams): UseAgentFilterPanelResult {
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )
  const isFilterOptionsLoading = useDefaultFilterListLoading(
    projectId ?? undefined,
  )
  const filterOptions = useMemo(
    () => narrowAgentFilterOptions(defaultFilterList),
    [defaultFilterList],
  )

  const selectionsByField = useMemo(
    () => buildSelectionsByField(criteria, filterOptions),
    [criteria, filterOptions],
  )
  const selectedFilterOptions = useMemo(
    () => [...selectionsByField.values()],
    [selectionsByField],
  )
  // Identity changes iff selectionsByField changes — FilterControl's memos
  // key off this reference.
  const getFilterSelection = useMemo(
    () =>
      (
        field: string,
      ): FilterControlProps['selectedFilterOptions'][number] | null =>
        selectionsByField.get(field) ?? null,
    [selectionsByField],
  )

  const updateFilterSelection = useMemo(
    () => buildUpdateFilterSelectionHandler(criteria, onCriteriaChange),
    [criteria, onCriteriaChange],
  )

  const getStaleInfo = useMemo(
    () => (staleByField ? (field: string) => staleByField[field] : undefined),
    [staleByField],
  )

  // Only with the server's map in hand is a missing theme uuid known to be
  // deleted: the map names every one that still resolves. Without it (not
  // fetched yet, or the lookup failed) the card keeps its raw-value fallback.
  const getValueLabel = useMemo(
    () =>
      valueLabels
        ? (field: string, value: string) => {
            if (Object.hasOwn(valueLabels, value)) return valueLabels[value]
            return field === 'theme' ? unavailableLabel(value) : undefined
          }
        : undefined,
    [valueLabels],
  )

  const filterControlProps = buildFilterControlProps({
    filterOptions,
    isFilterOptionsLoading,
    selectedFilterOptions,
    updateFilterSelection,
    getFilterSelection,
    getStaleInfo,
    getValueLabel,
  })

  return { filterControlProps }
}
